import { createSelector } from '@ngrx/store';
import { Decimal } from 'decimal.js';

import { betslipSelector } from '../../base/store/selectors';
import { ISucceededSlipResult } from '../betplacement/models';
import { isFailedSlip, isPartialSlip, isSucceededNonPartialSlip } from '../betplacement/utils';
import { LinearResultCount, LinearResultSummary, LinearResultSummaryViewModel } from './models/linear-result-summary.model';
import { getTokenWinningsBoost } from './utils/result-tokens.utils';

export const betslipResultStateSelector = createSelector(betslipSelector, (state) => state.result);
export const betslipResultSummaryStateSelector = createSelector(betslipSelector, (state) => state.result.summary);
export const betslipPlacedResultStateSelector = createSelector(betslipResultStateSelector, (state) => state.result);

export const selectPlacedResultSlips = createSelector(betslipPlacedResultStateSelector, (result) => result.slips);

export const selectKeepsAnyBetsInBetslip = createSelector(
    betslipResultStateSelector,
    (state) =>
        state.keepPlacedBetsInBetslip ||
        state.keepPlacedBetsInLinearCombo ||
        state.keepPlacedBetsInLinearSingle ||
        state.keepPlacedBetsInLinearTeaser ||
        Object.values(state.keepPlacedBetsInLinearBetBuilder).some((keep) => !!keep) ||
        Object.values(state.keepPlacedBetsInLinearSystem).some((keep) => !!keep),
);

export const selectLinearResultSummary = createSelector(
    selectPlacedResultSlips,
    (slips): LinearResultSummary =>
        slips.reduce(
            (arr, curr) => {
                if (isSucceededNonPartialSlip(curr)) {
                    const winningsBoost = getWinningsBoost(curr);

                    const possibleWinningsGross = new Decimal(curr.possibleWinnings?.gross || 0);
                    const possibleWinningsNet = new Decimal(curr.possibleWinnings?.net || 0);

                    const possibleWinningsGrossBoosted = possibleWinningsGross.greaterThan(0)
                        ? possibleWinningsGross.add(winningsBoost)
                        : possibleWinningsGross;
                    const possibleWinningsNetBoosted = possibleWinningsNet.greaterThan(0)
                        ? possibleWinningsNet.add(winningsBoost)
                        : possibleWinningsNet;

                    return {
                        ...arr,
                        numberOfTotalBets: arr.numberOfTotalBets + 1,
                        numberOfBetsPlaced: arr.numberOfBetsPlaced + 1,
                        totalStake: arr.totalStake + (curr.totalStake ?? 0),
                        totalReturnsGross: arr.totalReturnsGross.add(possibleWinningsGrossBoosted),
                        totalReturnsNet: arr.totalReturnsNet.add(possibleWinningsNetBoosted),
                    };
                }

                if (isPartialSlip(curr)) {
                    return {
                        ...arr,
                        numberOfTotalBets: arr.numberOfTotalBets + 1,
                        numberOfBetsPlaced: arr.numberOfBetsPlaced + 1,
                    };
                }

                if (isFailedSlip(curr)) {
                    return {
                        ...arr,
                        numberOfTotalBets: arr.numberOfTotalBets + 1,
                        numberOfFailedBets: arr.numberOfFailedBets + 1,
                    };
                }

                return {
                    ...arr,
                    numberOfTotalBets: arr.numberOfTotalBets + 1,
                };
            },
            {
                numberOfTotalBets: 0,
                numberOfBetsPlaced: 0,
                totalStake: 0,
                totalReturnsGross: new Decimal(0),
                totalReturnsNet: new Decimal(0),
                numberOfFailedBets: 0,
            },
        ),
);

export const selectHasSuccessSlipWithEmptyPossibleWinnings = createSelector(selectPlacedResultSlips, (slips) =>
    slips.some((slip) => isSucceededNonPartialSlip(slip) && !slip.possibleWinnings?.net && !slip.possibleWinnings?.gross),
);

export const selectLinearResultViewModel = createSelector(
    selectLinearResultSummary,
    selectKeepsAnyBetsInBetslip,
    selectHasSuccessSlipWithEmptyPossibleWinnings,
    (linearResult, keepsBets, hasSuccessSlipWithEmptyPossibleWinnings): LinearResultSummaryViewModel => ({
        ...linearResult,
        hasRemainingBetsInBetslip: keepsBets || linearResult.numberOfFailedBets > 0,
        showEmptyPossibleWinnings: hasSuccessSlipWithEmptyPossibleWinnings,
    }),
);

export const selectLinearResultCount = createSelector(
    selectLinearResultSummary,
    (summary): LinearResultCount => ({
        numberOfBetsPlaced: summary.numberOfBetsPlaced,
        numberOfFailedBets: summary.numberOfFailedBets,
        numberOfTotalBets: summary.numberOfTotalBets,
    }),
);

function getWinningsBoost(slip: ISucceededSlipResult): Decimal {
    const token = slip.promoTokens[0];

    return getTokenWinningsBoost(token);
}
